import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { ChevronDown, EllipsisVertical } from 'lucide-react';
import { useSidebar } from '../context/SidebarContext';
import SidebarWidget from './SidebarWidget';
import { getMenuForRole } from '../config/menuConfig';
import logo from '../assets/whub-logo.png';

const AppSidebar = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);

  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [subMenuHeight, setSubMenuHeight] = useState({});
  const subMenuRefs = useRef({});

  // Get role-based menu items
  const { main: navItems, others: othersItems } = useMemo(() => {
    if (!user?.role) {
      return { main: [], others: [] };
    }
    return getMenuForRole(user.role);
  }, [user?.role]);

  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);

  useEffect(() => {
    let submenuMatched = false;
    ['main', 'others'].forEach((menuType) => {
      const items = menuType === 'main' ? navItems : othersItems;
      items.forEach((nav, index) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (isActive(subItem.path)) {
              setOpenSubmenu({
                type: menuType,
                index,
              });
              submenuMatched = true;
            }
          });
        }
      });
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location, isActive]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index, menuType) => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (prevOpenSubmenu && prevOpenSubmenu.type === menuType && prevOpenSubmenu.index === index) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  const renderMenuItems = (items, menuType) => (
    <ul className='flex flex-col gap-4'>
      {items.map((nav, index) => {
        // Get the first path from subItems or use direct path
        const itemPath = nav.subItems && nav.subItems.length > 0 ? nav.subItems[0].path : nav.path;

        // Check if icon is an image path (string) or a component
        const isImageIcon = typeof nav.icon === 'string';

        return (
          <li key={nav.name}>
            {nav.subItems && nav.subItems.length > 1 ? (
              // Has multiple sub-items, render as dropdown
              <>
                <button
                  onClick={() => handleSubmenuToggle(index, menuType)}
                  className={`menu-item group ${
                    openSubmenu?.type === menuType && openSubmenu?.index === index
                      ? 'menu-item-active'
                      : 'menu-item-inactive'
                  } cursor-pointer ${
                    !isExpanded && !isHovered ? 'lg:justify-center' : 'lg:justify-start'
                  }`}
                >
                  <span
                    className={`menu-item-icon-size  ${
                      openSubmenu?.type === menuType && openSubmenu?.index === index
                        ? 'menu-item-icon-active'
                        : 'menu-item-icon-inactive'
                    }`}
                  >
                    {isImageIcon ? (
                      <img src={nav.icon} alt={nav.name} className='w-6 h-6' />
                    ) : (
                      nav.icon
                    )}
                  </span>
                  {(isExpanded || isHovered || isMobileOpen) && (
                    <span className='menu-item-text'>{nav.name}</span>
                  )}
                  {(isExpanded || isHovered || isMobileOpen) && (
                    <ChevronDown
                      className={`ml-auto w-5 h-5 transition-transform duration-200 ${
                        openSubmenu?.type === menuType && openSubmenu?.index === index
                          ? 'rotate-180 text-brand-500'
                          : ''
                      }`}
                    />
                  )}
                </button>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <div
                    ref={(el) => {
                      subMenuRefs.current[`${menuType}-${index}`] = el;
                    }}
                    className='overflow-hidden transition-all duration-300'
                    style={{
                      height:
                        openSubmenu?.type === menuType && openSubmenu?.index === index
                          ? `${subMenuHeight[`${menuType}-${index}`]}px`
                          : '0px',
                    }}
                  >
                    <ul className='mt-2 space-y-1 ml-9'>
                      {nav.subItems.map((subItem) => (
                        <li key={subItem.name}>
                          <Link
                            to={subItem.path}
                            className={`menu-dropdown-item ${
                              isActive(subItem.path)
                                ? 'menu-dropdown-item-active'
                                : 'menu-dropdown-item-inactive'
                            }`}
                          >
                            {subItem.name}
                            <span className='flex items-center gap-1 ml-auto'>
                              {subItem.new && (
                                <span
                                  className={`ml-auto ${
                                    isActive(subItem.path)
                                      ? 'menu-dropdown-badge-active'
                                      : 'menu-dropdown-badge-inactive'
                                  } menu-dropdown-badge`}
                                >
                                  new
                                </span>
                              )}
                              {subItem.pro && (
                                <span
                                  className={`ml-auto ${
                                    isActive(subItem.path)
                                      ? 'menu-dropdown-badge-active'
                                      : 'menu-dropdown-badge-inactive'
                                  } menu-dropdown-badge`}
                                >
                                  pro
                                </span>
                              )}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              // Single item or single sub-item, render as direct link
              itemPath && (
                <Link
                  to={itemPath}
                  className={`menu-item group ${
                    isActive(itemPath) ? 'menu-item-active' : 'menu-item-inactive'
                  }`}
                >
                  <span
                    className={`menu-item-icon-size ${
                      isActive(itemPath) ? 'menu-item-icon-active' : 'menu-item-icon-inactive'
                    }`}
                  >
                    {isImageIcon ? (
                      <img src={nav.icon} alt={nav.name} className='w-6 h-6' />
                    ) : (
                      nav.icon
                    )}
                  </span>
                  {(isExpanded || isHovered || isMobileOpen) && (
                    <span className='menu-item-text'>{nav.name}</span>
                  )}
                </Link>
              )
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 left-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200 
        ${isExpanded || isMobileOpen ? 'w-[290px]' : isHovered ? 'w-[290px]' : 'w-[90px]'} 
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`py-2 flex justify-center ${
          !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-centre'
        }`}
      >
        <Link to='/'>
          {isExpanded || isHovered || isMobileOpen ? (
            <>
              <img className='dark:hidden' src={logo} alt='Logo' width={100} height={40} />
              <img className='hidden dark:block' src={logo} alt='Logo' width={100} height={40} />
            </>
          ) : (
            <img src={logo} alt='Logo' width={100} height={100} />
          )}
        </Link>
      </div>
      <div className='flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar'>
        <nav className='mb-6'>
          <div className='flex flex-col gap-4'>
            <div>
              <h2
                className={`mb-4 text-xs uppercase flex leading-[20px] text-gray-400 ${
                  !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start'
                }`}
              >
                {isExpanded || isHovered || isMobileOpen ? (
                  'Menu'
                ) : (
                  <ChevronDown className='size-6' />
                )}
              </h2>
              {renderMenuItems(navItems, 'main')}
            </div>
            <div className=''>
              <h2
                className={`mb-4 text-xs uppercase flex leading-[20px] text-gray-400 ${
                  !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start'
                }`}
              >
                {isExpanded || isHovered || isMobileOpen ? 'Others' : <EllipsisVertical />}
              </h2>
              {renderMenuItems(othersItems, 'others')}
            </div>
          </div>
        </nav>
        {isExpanded || isHovered || isMobileOpen ? <SidebarWidget /> : null}
      </div>
    </aside>
  );
};

export default AppSidebar;
